// FAL.AI provider — verified working against openrouter/router endpoint
// 2026-04-27 with model openai/gpt-5.5.
//
// Three call shapes are exposed:
//   callFal()             — text-only chat (openrouter/router, prompt + system_prompt)
//   callFalVision()       — chat + image attachments (openrouter/router with messages[])
//   callFalImageEdit()    — image-to-image generation (openai/gpt-image-2/edit)
//
// All three use the same FAL key. Images are passed as data URIs (base64) so we
// skip the FAL storage round-trip — endpoints accept either URLs or data URIs
// in `image_urls` / `messages[].content[].image_url`.

const ENDPOINT_TEXT  = 'https://fal.run/openrouter/router';
const ENDPOINT_IMAGE = 'https://fal.run/openai/gpt-image-2/edit';

export interface ToolCall {
  tool: string;
  args: Record<string, unknown>;
}

/** Legacy OpenAI-style tool descriptor — retained for graphTools.TOOL_DEFS,
 *  which we keep as documentation even though we use the JSON-in-output
 *  protocol instead of native tool_calls. */
export interface ToolDef {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
}

export interface ChatRequest {
  apiKey: string;
  model: string;             // OpenRouter slug, e.g. "openai/gpt-5.5"
  systemPrompt: string;
  prompt: string;
  /** Hard cap on output tokens. Defaults to 4096 — most graph-mutation
   *  responses fit in well under that, and the cap prevents a runaway
   *  model from burning $5 of credit on a 50k-token reply.
   *  CRITICAL: openrouter/router does honor this. Without it, models can
   *  output 30k+ tokens per turn unchecked. */
  maxTokens?: number;
}

export interface VisionRequest {
  apiKey: string;
  model: string;
  systemPrompt: string;
  prompt: string;
  /** Data URIs ("data:image/png;base64,...") or public URLs. */
  imageUrls: string[];
  maxTokens?: number;
}

export interface ImageEditRequest {
  apiKey: string;
  /** Source image(s) to edit. Data URI or URL. */
  imageUrls: string[];
  prompt: string;
  quality?: 'low' | 'medium' | 'high';
  /** FAL enum — NOT WxH strings. Use 'square_hd' for 1024² technical sheets. */
  imageSize?: 'square_hd' | 'square' | 'portrait_4_3' | 'portrait_16_9' | 'landscape_4_3' | 'landscape_16_9' | 'auto';
}

export interface UsageInfo {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  cost?: number;
}

export interface RawResponse {
  output: string;
  reasoning?: string | null;
  error?: string | null;
  usage?: UsageInfo;
}

export interface ImageEditResponse {
  /** First generated image as data URI ("data:image/png;base64,..."). Convenient for
   *  immediately rendering in <img> tags or feeding back into another vision call. */
  dataUri: string;
  /** Raw FAL hosted URL for sharing/persisting. */
  url: string;
  width: number;
  height: number;
}

function authHeaders(apiKey: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Key ${apiKey}`
  };
}

function describeHttpError(status: number, text: string, model: string): string {
  let hint = '';
  if (status === 401 || status === 403) hint = ' — API anahtarı geçersiz veya süresi dolmuş.';
  else if (status === 422 || /invalid.*model|model.*not.*found|unknown.*model/i.test(text))
    hint = ` — "${model}" modeli bulunamadı veya istek şeması reddedildi.`;
  else if (status === 429) hint = ' — rate limit dolu, biraz bekle.';
  else if (status === 402) hint = ' — kredi yetersiz; FAL dashboard\'unda bakiyeyi kontrol et.';
  return `HTTP ${status}${hint}\n${text.slice(0, 360)}`;
}

/** Single round-trip to OpenRouter via FAL — text only. */
export async function callFal(req: ChatRequest): Promise<RawResponse> {
  if (!req.apiKey) throw new Error('FAL.AI API anahtarı boş');
  const body = {
    model: req.model,
    prompt: req.prompt,
    system_prompt: req.systemPrompt,
    max_tokens: req.maxTokens ?? 4096
  };
  const res = await fetch(ENDPOINT_TEXT, {
    method: 'POST',
    headers: authHeaders(req.apiKey),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('OpenRouter ' + describeHttpError(res.status, text, req.model));
  }
  const json = await res.json();
  if (json.error) throw new Error('Model hatası: ' + json.error);
  return {
    output: typeof json.output === 'string' ? json.output : JSON.stringify(json),
    reasoning: json.reasoning,
    error: json.error,
    usage: json.usage
  };
}

/** Vision-enabled chat. Builds an OpenAI-style messages[] payload — the
 *  openrouter/router FAL wrapper passes this through to the underlying model.
 *  System prompt + user text + N image attachments in one turn.
 *  Falls back to text-only with markdown image links if the multimodal shape
 *  is rejected (some FAL routes only accept `prompt`). */
export async function callFalVision(req: VisionRequest): Promise<RawResponse> {
  if (!req.apiKey) throw new Error('FAL.AI API anahtarı boş');

  const userContent: unknown[] = [{ type: 'text', text: req.prompt }];
  for (const url of req.imageUrls) {
    userContent.push({ type: 'image_url', image_url: { url } });
  }

  // Try multimodal messages shape first (works with OpenRouter direct + most FAL wrappers).
  const bodyMM = {
    model: req.model,
    messages: [
      { role: 'system', content: req.systemPrompt },
      { role: 'user', content: userContent }
    ],
    max_tokens: req.maxTokens ?? 4096
  };
  let res = await fetch(ENDPOINT_TEXT, {
    method: 'POST',
    headers: authHeaders(req.apiKey),
    body: JSON.stringify(bodyMM)
  });

  // Fallback: same call but with `prompt` containing markdown image refs.
  // Vision-capable models often still see images via markdown when the wrapper
  // doesn't surface the messages shape.
  if (res.status === 422 || res.status === 400) {
    const md = req.imageUrls.map((u, i) => `![image-${i + 1}](${u})`).join('\n');
    const bodyText = {
      model: req.model,
      prompt: `${req.prompt}\n\n${md}`,
      system_prompt: req.systemPrompt
    };
    res = await fetch(ENDPOINT_TEXT, {
      method: 'POST',
      headers: authHeaders(req.apiKey),
      body: JSON.stringify(bodyText)
    });
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('OpenRouter Vision ' + describeHttpError(res.status, text, req.model));
  }
  const json = await res.json();
  if (json.error) throw new Error('Model hatası: ' + json.error);

  // Multimodal response shapes: OpenAI = choices[0].message.content (string).
  // FAL openrouter/router = output (string). Try both.
  let output: string;
  if (typeof json.output === 'string') {
    output = json.output;
  } else if (json.choices?.[0]?.message?.content) {
    const c = json.choices[0].message.content;
    output = typeof c === 'string' ? c : JSON.stringify(c);
  } else {
    output = JSON.stringify(json);
  }
  return { output, reasoning: json.reasoning, error: json.error, usage: json.usage };
}

/** GPT-Image-2 edit — generates a new image from one or more reference images
 *  plus a text prompt. Used in the parametric pipeline to synthesize a
 *  multi-view technical sheet from a single user photo. */
export async function callFalImageEdit(req: ImageEditRequest): Promise<ImageEditResponse> {
  if (!req.apiKey) throw new Error('FAL.AI API anahtarı boş');
  const body = {
    image_urls: req.imageUrls,
    prompt: req.prompt,
    quality: req.quality ?? 'high',
    image_size: req.imageSize ?? 'auto',
    num_images: 1,
    output_format: 'png',
    sync_mode: true        // returns data URI inline — no second roundtrip
  };
  const res = await fetch(ENDPOINT_IMAGE, {
    method: 'POST',
    headers: authHeaders(req.apiKey),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error('GPT-Image-2 ' + describeHttpError(res.status, text, 'openai/gpt-image-2'));
  }
  const json = await res.json();
  const img = json.images?.[0];
  if (!img?.url) throw new Error('GPT-Image-2 boş yanıt: ' + JSON.stringify(json).slice(0, 200));
  // sync_mode returns a data URI in `url` already; non-sync returns hosted URL.
  const url = img.url as string;
  return {
    dataUri: url.startsWith('data:') ? url : url,   // pass through; Vision call accepts both
    url,
    width: img.width ?? 1024,
    height: img.height ?? 1024
  };
}

/** Lightweight ping — verifies endpoint, auth, and model slug in isolation. */
export async function testConnection(apiKey: string, model: string): Promise<string> {
  const r = await callFal({
    apiKey,
    model,
    systemPrompt: 'Reply with exactly the word: pong',
    prompt: 'ping'
  });
  return r.output.trim();
}

/** Parse the model's structured output. Looks for:
 *    <actions>[ ... ]</actions>     — JSON array of {tool, args}
 *    <reply>...</reply>             — user-facing message
 *  Both tags are optional. If no <reply>, the entire output (minus actions) is
 *  treated as the reply. If no <actions>, returns empty list.
 *
 *  Tolerant to ```json fences, surrounding markdown, and missing closing tags. */
export function parseModelOutput(raw: string): { actions: ToolCall[]; reply: string } {
  let actions: ToolCall[] = [];
  let reply = raw;

  const actionMatch = raw.match(/<actions>([\s\S]*?)<\/actions>/i);
  if (actionMatch) {
    let body = actionMatch[1].trim();
    body = body.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
    try {
      const parsed = JSON.parse(body);
      if (Array.isArray(parsed)) {
        actions = parsed.filter(
          (x): x is ToolCall => x && typeof x.tool === 'string' && typeof x.args === 'object'
        );
      }
    } catch {
      // Malformed JSON — leave actions empty, surface raw to user as reply.
    }
    reply = raw.replace(actionMatch[0], '').trim();
  }

  const replyMatch = reply.match(/<reply>([\s\S]*?)<\/reply>/i);
  if (replyMatch) reply = replyMatch[1].trim();

  return { actions, reply };
}

/** Read a File as data URI ("data:image/png;base64,..."). Used to feed user
 *  uploads into both image-edit and vision chat calls without going through
 *  FAL storage. */
export function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Resim okunamadı'));
    r.onload = () => resolve(r.result as string);
    r.readAsDataURL(file);
  });
}
